import React, { useState } from 'react';
import { Col, Form, Row } from 'antd';
import type { FormInstance } from 'antd';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Card, Input, Select, Button, BUTTON_TYPES, notification, NOTIFICATION_TYPES } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { ACTIONS, Can, UI } from 'permission';
import { fetchWrapper } from 'helpers/helpers';
import { useDebounceEffect } from 'helpers/useDebounceEffect';
import { BUTTON_TEXT } from 'constants/text';
import { ALL_LIMIT } from 'constants/constants';
import { ReactComponent as Arrow } from 'dist/icons/Vector1.svg';
import type { SelectOption } from '_atoms/Select/types';
import { OPTIONS_REQUEST_CONFIG, FIELDS_HIERARCHY, EMPTY_FILTERS_VALUES } from './constants';
import { optionsMappers, searchFiltersMapper } from './utils';
import './style.scss';

interface FiltersJobProps {
  form: FormInstance;
  initialValues: Record<string, unknown>;
  onFinish: (values: Record<string, unknown>) => void;
  onReset?: () => void;
  loading?: boolean;
}

const FiltersJob: React.FC<FiltersJobProps> = ({ form, initialValues, onFinish, onReset, loading }) => {
  const { t } = useTranslation();
  const [filtersLoading, setFiltersLoading] = useState<Record<string, boolean>>({});
  const [filterOptions, setFilterOptions] = useState<Record<string, SelectOption[]>>({});
  const [openDetails, onOpenDetails] = useState(false);
  const onSearchDebounce = (type: string, search = '') => {
    if (filtersLoading[type]) return;
    setFiltersLoading(f => ({ ...f, [type]: true }));
    fetchWrapper(OPTIONS_REQUEST_CONFIG[type](search, searchFiltersMapper(form.getFieldsValue())))
      .then(options => {
        setFilterOptions(o => ({ ...o, [type]: optionsMappers[type](options) }));
        setFiltersLoading(f => ({ ...f, [type]: false }));
      })
      .catch(() => {
        setFilterOptions(o => ({ ...o, [type]: [] }));
        setFiltersLoading(f => ({ ...f, [type]: false }));
      });
  };

  const onSearch = useDebounceEffect(onSearchDebounce, 300);

  const fetchAll = (type: string) => () =>
    fetchWrapper(OPTIONS_REQUEST_CONFIG[type]('', searchFiltersMapper(form.getFieldsValue()), ALL_LIMIT))
      .then(options => {
        const mapped = optionsMappers[type](options);
        setFilterOptions(o => ({ ...o, [type]: mapped }));
        return mapped;
      })
      .catch(() => {
        notification({ type: NOTIFICATION_TYPES.ERROR, message: t('translation:bulk_select.load_all_error') });
        return [];
      });

  const typeToSearch = () => onFinish(form.getFieldsValue());

  const onValuesChange = values => {
    const field = Object.keys(values)[0];
    form.setFields(FIELDS_HIERARCHY[field]);
  };

  return (
    <Card className={cn('jobs-filters', { open: openDetails })}>
      <div className="jobs-filters__header">
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{t('translation:filters_job.title')}</Typography.Title2>
        <Button
          className={cn('open-details original-svg', { open: openDetails })}
          onClick={() => onOpenDetails(o => !o)}
          type={BUTTON_TYPES.GHOST}
          icon={<Arrow />}
        />
      </div>
      <Form form={form} onFinish={onFinish} initialValues={initialValues} onValuesChange={onValuesChange}>
        <Input
          placeholder={t('translation:filters_job.ph_search_by_job_name')}
          type="search"
          onDebounceChange={typeToSearch}
          itemProps={{ name: 'search' }}
          className="filter-job-name"
        />
        <div className="jobs-filters__body">
          <Row gutter={[32, 0]}>
            <Can I={ACTIONS.READ} a={UI.JOB_LIST_COMPANIES} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_job.label_companies')}
                    placeholder={t('translation:filters_job.ph_companies')}
                    options={filterOptions.companies}
                    loading={filtersLoading.companies}
                    onSearch={v => onSearch('companies', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('companies')}
                    fetchAllOptions={fetchAll('companies')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'companies' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.JOB_LIST_SERVICE} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_job.label_service')}
                    placeholder={t('translation:filters_job.ph_service')}
                    options={filterOptions.services}
                    loading={filtersLoading.services}
                    onSearch={v => onSearch('services', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('services')}
                    fetchAllOptions={fetchAll('services')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'services' }}
                  />
                </Col>
              )}
            </Can>
          </Row>
          <Row gutter={[32, 0]}>
            <Can I={ACTIONS.READ} a={UI.JOB_LIST_TEAM_LEAD} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_job.label_team_lead')}
                    placeholder={t('translation:filters_job.ph_team_lead')}
                    options={filterOptions.team_leads}
                    loading={filtersLoading.team_leads}
                    onSearch={v => onSearch('team_leads', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('team_leads')}
                    fetchAllOptions={fetchAll('team_leads')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'team_leads' }}
                  />
                </Col>
              )}
            </Can>
            <Col span={24} lg={24} md={12} sm={24}>
              <Select
                labelInValue
                label={t('translation:filters_job.label_opening_team_lead')}
                placeholder={t('translation:filters_job.ph_opening_team_lead')}
                options={filterOptions.opening_team_leads}
                loading={filtersLoading.opening_team_leads}
                onSearch={v => onSearch('opening_team_leads', v)}
                onDropdownVisibleChange={open => open && onSearchDebounce('opening_team_leads')}
                fetchAllOptions={fetchAll('opening_team_leads')}
                mode={SELECT_TYPES.MULTIPLE}
                filterOption={false}
                itemProps={{ name: 'opening_team_leads' }}
              />
            </Col>
          </Row>
          <Row gutter={[32, 0]}>
            <Can I={ACTIONS.READ} a={UI.JOB_LIST_JOB_STATUS} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_job.label_job_status')}
                    placeholder={t('translation:filters_job.ph_job_status')}
                    options={EMPTY_FILTERS_VALUES.jobs_statuses}
                    mode={SELECT_TYPES.MULTIPLE}
                    itemProps={{ name: 'jobs_statuses' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.JOB_LIST_RECRUITER} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_job.label_recruiter')}
                    placeholder={t('translation:filters_job.ph_recruiter')}
                    options={filterOptions.recruiters}
                    loading={filtersLoading.recruiters}
                    onSearch={v => onSearch('recruiters', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('recruiters')}
                    fetchAllOptions={fetchAll('recruiters')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'recruiters' }}
                  />
                </Col>
              )}
            </Can>
          </Row>
          <Row gutter={[32, 0]}>
            <Can I={ACTIONS.READ} a={UI.JOB_LIST_OPENING_STATUS} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_job.label_opening_status')}
                    placeholder={t('translation:filters_job.ph_opening_status')}
                    options={EMPTY_FILTERS_VALUES.openings_statuses}
                    mode={SELECT_TYPES.MULTIPLE}
                    itemProps={{ name: 'openings_statuses' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.JOB_LIST_EXPERIENCE_LEVEL} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_job.label_experience_level')}
                    placeholder={t('translation:filters_job.ph_experience_level')}
                    options={filterOptions.experience_levels}
                    loading={filtersLoading.experience_levels}
                    onSearch={v => onSearch('experience_levels', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('experience_levels')}
                    fetchAllOptions={fetchAll('experience_levels')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'experience_levels' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.JOB_LIST_OFFICE} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_job.label_office')}
                    placeholder={t('translation:filters_job.ph_office_location')}
                    options={filterOptions.offices}
                    loading={filtersLoading.offices}
                    onSearch={v => onSearch('offices', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('offices')}
                    fetchAllOptions={fetchAll('offices')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'offices' }}
                  />
                </Col>
              )}
            </Can>
          </Row>
          <div className="jobs-filters__footer">
            <Button type={BUTTON_TYPES.SECONDARY} onClick={loading ? undefined : onReset}>
              {BUTTON_TEXT.RESET_FILTERS}
            </Button>
            <Button type={BUTTON_TYPES.PRIMARY} htmlType={loading ? 'button' : 'submit'}>
              {BUTTON_TEXT.APPLY}
            </Button>
          </div>
        </div>
      </Form>
    </Card>
  );
};

export default FiltersJob;
